import "./style.css";
import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";

import { Layout } from "@/components/Layout";
import { PreferenceSettingsMenu } from "@/components/PreferenceSettingsMenu";
import { ProtectedRoute, PublicRoute } from "@/components/ProtectedRoute";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { useLanguage } from "@/contexts/LanguageContext";
const AgendaPage = lazy(() => import("@/pages/AgendaPage/AgendaPage"));
const AtendimentoPage = lazy(() => import("@/pages/AtendimentoPage/AtendimentoPage"));
const CatalogPage = lazy(() => import("@/pages/CatalogPage"));
const ClinicalTemplatesPage = lazy(() => import("@/pages/ClinicalTemplatesPage"));
const ClinicalProductionPage = lazy(() => import("@/pages/ClinicalProductionPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage/DashboardPage"));
const EvaluationSheetPage = lazy(() => import("@/pages/EvaluationSheetPage"));
const ManagementOverviewPage = lazy(() => import("@/pages/ManagementOverviewPage"));
const ExpensesPage = lazy(() => import("@/pages/ExpensesPage"));
const FinanceiroPage = lazy(() => import("@/pages/FinanceiroPage/FinanceiroPage"));
const HomePage = lazy(() => import("@/pages/HomePage/HomePage"));
const ManagerialIncomeStatementPage = lazy(() => import("@/pages/ManagerialIncomeStatementPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage/LeadsPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage/MarketingPage"));
const MetasPage = lazy(() => import("@/pages/MetasPage"));
const PatientsPage = lazy(() => import("@/pages/PatientsPage/PatientsPage"));
const ProfessionalsPage = lazy(() => import("@/pages/ProfessionalsPage/ProfessionalsPage"));
const ProfessionalPayoutsPage = lazy(() => import("@/pages/ProfessionalPayoutsPage"));
const SectorsPage = lazy(() => import("@/pages/SectorsPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage/SettingsPage"));
const PublicNpsPage = lazy(() => import("@/pages/PublicNpsPage/PublicNpsPage"));
const SuperAdminPage = lazy(() => import("@/pages/SuperAdminPage/SuperAdminPage"));
const UsersPage = lazy(() => import("@/pages/UsersPage/UsersPage"));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));

function RouteLoadingFallback() {
  const { t } = useLanguage();

  return (
    <div className="flex min-h-screen items-center justify-center bg-clinic-bg" role="status" aria-live="polite">
      <div className="text-center">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-clinic-primary/30 border-t-clinic-primary" />
        <p className="text-clinic-text-secondary">{t("Carregando...")}</p>
      </div>
    </div>
  );
}

function RouteAwareFloatingControls() {
  const location = useLocation();
  const isPublicRoute =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname.startsWith("/nps/");

  if (!isPublicRoute) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-3">
      <PreferenceSettingsMenu />
    </div>
  );
}

function AuthenticatedLanguageProvider({ children }: { children: ReactNode }) {
  const { selectedClinic } = useAuth();
  return <LanguageProvider clinic={selectedClinic}>{children}</LanguageProvider>;
}

function App() {
  return (
    <AuthProvider>
      <AuthenticatedLanguageProvider>
        <ThemeProvider>
          <BrowserRouter>
            <Suspense fallback={<RouteLoadingFallback />}>
              <Routes>
              <Route path="/nps/:token" element={<PublicNpsPage />} />
              <Route path="/" element={<HomePage />} />
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <RegisterPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="dashboard.read">
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leads"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="leads.read">
                    <Layout>
                      <LeadsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/marketing"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="marketing.read">
                    <Layout>
                      <MarketingPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pacientes"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="patients.read">
                    <Layout>
                      <PatientsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ficha-avaliacao"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="patients.read">
                    <ProtectedRoute requiredPermission="medical_records.read">
                      <Layout>
                        <EvaluationSheetPage />
                      </Layout>
                    </ProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profissionais"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="professionals.read">
                    <Layout>
                      <ProfessionalsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/agenda"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="appointments.read">
                    <Layout>
                      <AgendaPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="finance.read">
                    <Layout>
                      <FinanceiroPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/despesas"
                element={
                  <ProtectedRoute requiredPermission="expenses.read">
                    <Layout>
                      <ExpensesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/visao-gerencial"
                element={
                  <ProtectedRoute requiredPermission="management_overview.read">
                    <Layout>
                      <ManagementOverviewPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/dre-gerencial"
                element={
                  <ProtectedRoute requiredPermission="managerial_income_statement.read">
                    <Layout>
                      <ManagerialIncomeStatementPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financeiro/repasses"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="payouts.read">
                    <Layout>
                      <ProfessionalPayoutsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/metas"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="goals.read">
                    <Layout>
                      <MetasPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/atendimento"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="attendance.read">
                    <Layout>
                      <AtendimentoPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/atendimento/producao"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="clinical_production.read">
                    <Layout>
                      <ClinicalProductionPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/usuarios"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="users.read">
                    <Layout>
                      <UsersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Layout>
                      <SuperAdminPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin/clinicas"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Layout>
                      <SuperAdminPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin/clínicas"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Layout>
                      <SuperAdminPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/configuracoes/*"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/setores"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "PROFESSIONAL"]} requiredPermission="professionals.read">
                    <Layout>
                      <SectorsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalogo"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <Layout>
                      <CatalogPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/templates-clinicos"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN"]}>
                    <Layout>
                      <ClinicalTemplatesPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin/super-admins"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Layout>
                      <SuperAdminPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin/importa-zen-fisio"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Layout>
                      <SuperAdminPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin/auditoria"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Layout>
                      <SuperAdminPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              </Routes>
            </Suspense>
            <RouteAwareFloatingControls />
          </BrowserRouter>
          <Toaster />
        </ThemeProvider>
      </AuthenticatedLanguageProvider>
    </AuthProvider>
  );
}

export default App;
